﻿import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Modal, Button, InputGroup } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";

import { formatMilesDecimalNew, hasPermission, integerValidate, timeValidate, toast } from "@/services/Utils";
import { useEstatesParent } from "@/hooks/maintainers/estatesParents";

import Fetching from "@/components/Fetching/Fetching";
import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { customStyles } from "@/components/ReactSelect/NoOptions.styles";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";

import EstatesDaysSelector from "./EstatesDaysSelector";
import { days } from "./estatesParentsDays";
import { base_element } from "../estatesparents.constants";
import classes from "../../maintainers.module.scss";

const EstatesParents = ({ confirmSave, isLoadingSave, permissions, estates, regions }) => {
	const navigate = useNavigate();
	const { addToast } = useToasts();
	const location = useLocation();
	const { option, slug } = useParams();

	const { customer: customerConfig } = useSelector((state) => state.auth.user);

	const [localStates, setLocalStates] = useState([]);
	const [localRegions, setLocalRegions] = useState([]);
	const [localities, setLocalities] = useState([]);
	const [data, setData] = useState({ ...base_element });

	const { data: response, isLoading } = useEstatesParent(slug);

	useEffect(() => {
		if (response) {
			setData(response.detail);
		}

		if (estates) {
			setLocalStates(estates);
		}

		if (regions) {
			setLocalRegions(regions);
		}
	}, [response, estates, regions]);

	useEffect(() => {
		if (data.region && localRegions.length > 0) {
			const selectedRegion = localRegions.find((r) => r.value === data.region);
			if (selectedRegion && selectedRegion.localities) {
				setLocalities(selectedRegion.localities);
			}
		}
	}, [data.region, localRegions]);

	const handleClose = () => {
		if (location.state) {
			navigate(location.state);
			return;
		}

		navigate("/configuracion/grupos_campos");
	};

	const validSave = () => {
		if (!data.name) {
			toast(addToast, "Debe ingresar un nombre para guardar.", "error");
			return false;
		}

		return true;
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSave(data);
		}
	};

	return (
		<Modal show={option ? true : false} onHide={handleClose} backdrop="static" keyboard={false} className={classes.main_form} size="lg">
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Campo</h4>
			</Modal.Header>

			<Modal.Body>
				{isLoading || isLoadingSave ? (
					<Fetching topMargin={"1em"} />
				) : (
					<Row className={classes.row_form}>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Nombre</Form.Label>
								<Form.Control
									value={data ? data.name : ""}
									onChange={(e) =>
										setData({
											...data,
											name: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>
									{customerConfig.customer_screens.find((cs) => cs.screen_id === 51)
										? customerConfig.customer_screens.find((cs) => cs.screen_id === 51)?.name
										: "Campos"}
								</Form.Label>
								<CreatableSelect
									value={localStates?.filter((o) => data.estates?.includes(o.value) || data.estates?.includes(o.id))}
									options={localStates}
									menuPortalTarget={document.body}
									components={{ NoOptionsMessage }}
									formatCreateLabel={(value) => {
										return `Crear ${value}`;
									}}
									placeholder={<div>Seleccione</div>}
									isMulti={true}
									styles={customStyles}
									onChange={(opt, meta) => {
										if (meta.action === "create-option") {
											let newOption = {
												value: meta.option.value,
												label: meta.option.label,
											};
											setLocalStates([...estates, newOption]);
										}

										setData({
											...data,
											estates: opt.map((o) => o.value),
										});
									}}
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Región</Form.Label>
								<CustomInputSelect
									name="region"
									options={localRegions}
									value={data.region}
									placeholder="Seleccione región"
									isClearable
									onChange={(value) => {
										setData({ ...data, region: value ?? null, locality: null });
										const selectedRegion = localRegions?.find((r) => r.value === value);
										setLocalities(selectedRegion?.localities || []);
									}}
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Comuna</Form.Label>
								<CustomInputSelect
									name="locality"
									options={localities}
									value={data.locality}
									placeholder="Seleccione comuna"
									isClearable
									disabled={!localities || localities.length === 0}
									onChange={(value) => setData({ ...data, locality: value ?? null })}
								/>
							</Form.Group>
						</Col>

						{customerConfig.use_estates_parents && (
							<>
								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Cant. Hectáreas</Form.Label>

										<Form.Control
											value={data.hectares ? formatMilesDecimalNew(data.hectares) : ""}
											onChange={(e) => {
												let newValue = String(e.currentTarget.value).replaceAll(".", "");
												setData({
													...data,
													hectares: newValue,
												});
											}}
											onKeyDown={integerValidate}
											style={{ textAlign: "right" }}
											type="text"
											readOnly={true}
										/>
									</Form.Group>
								</Col>

								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Hora Apertura</Form.Label>

										<Form.Control
											value={data.time_open}
											onChange={(e) =>
												setData({
													...data,
													time_open: e.currentTarget.value,
												})
											}
											onKeyDown={timeValidate}
											type="time"
										/>
									</Form.Group>
								</Col>

								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Hora Cierre</Form.Label>

										<Form.Control
											value={data.time_close}
											onChange={(e) =>
												setData({
													...data,
													time_close: e.currentTarget.value,
												})
											}
											onKeyDown={timeValidate}
											type="time"
										/>
									</Form.Group>
								</Col>

								<Col md={12} xs={12}>
									{data.day_selection === false ? (
										<>
											<Form.Group className={classes.group_form}>
												<Form.Label className={classes.label}>Frecuencia de Notificación</Form.Label>

												<InputGroup className="mb-3">
													<InputGroup.Text
														style={{
															borderTopRightRadius: "0px",
															borderBottomRightRadius: "0px",
														}}
													>
														Cada
													</InputGroup.Text>
													<Form.Control
														value={data.notification_range ? data.notification_range : ""}
														onChange={(e) =>
															setData({
																...data,
																notification_range: e.target.value,
															})
														}
														type="text"
														onKeyDown={integerValidate}
													/>
													<InputGroup.Text
														style={{
															borderTopLeftRadius: "0px",
															borderBottomLeftRadius: "0px",
														}}
													>
														días
													</InputGroup.Text>
												</InputGroup>
											</Form.Group>

											<p
												onClick={() => {
													setData({
														...data,
														notification_range: "",
														day_selection: true,
													});
												}}
												style={{ color: "#549112" }}
											>
												O seleciona un día a la semana
											</p>
										</>
									) : (
										<>
											<EstatesDaysSelector
												notificationDays={data.days}
												setNotificationDays={(days) =>
													setData({
														...data,
														days: days,
													})
												}
												days={days}
											/>
											<p
												onClick={() => {
													setData({
														...data,
														days: [],
														day_selection: false,
													});
												}}
												style={{ color: "#549112" }}
											>
												Seleccionar intervalo de días
											</p>
										</>
									)}
								</Col>
							</>
						)}
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer>
				{(data.id ? hasPermission("edit", permissions) : hasPermission("create", permissions)) && !isLoading && (
					<Button variant="success" onClick={handleSave} disabled={isLoading || (isLoadingSave && true)}>
						GUARDAR
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default EstatesParents;
